import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ClipboardList, Users, MailCheck, GraduationCap, Layers, Target, Award, ChevronRight, Handshake,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useOutletContext } from "react-router-dom";
import BitrixContactForm from "../components/home/BitrixContactForm";

/**
 * İş Ortağım Programı — landing page for Fırat Real Estate's partner program.
 * Content adapted from https://www.firatrealestate.com/is-ortagim-programi/
 * Bottom of the page hosts the Bitrix "İş Ortaklığı" form filtered to that
 * single topic so all submissions land in the partnership Bitrix funnel.
 */

const STAGES = [
  {
    icon: ClipboardList,
    title: "Kayıt Oluşturma",
    body: "Programa katılmak isteyen adaylar, kısa bir ön kayıt formu doldurarak süreci başlatır. Formda iletişim bilgileri, gayrimenkul deneyimi ve motivasyon detayları yer alır.",
    accent: "#22346B", // Fırat navy
    tone: "light",
  },
  {
    icon: Users,
    title: "Değerlendirme Aşaması",
    body: "Başvurular Fırat Yapı İnsan Kaynakları ve Eğitim Departmanı tarafından dikkatle incelenir. Uygun görülen adaylar ön görüşme veya online değerlendirme sürecine davet edilir.",
    accent: "#2E4590", // primary bright
    tone: "light",
  },
  {
    icon: MailCheck,
    title: "Davet ve Katılım Onayı",
    body: "Değerlendirme sonrasında seçilen adaylar programa katılmaya hak kazanır. Eğitim tarihleri, giriş bilgileri ve materyaller e-posta veya WhatsApp üzerinden paylaşılır.",
    accent: "#1A2A5A", // navy
    tone: "dark",
  },
  {
    icon: GraduationCap,
    title: "Eğitim Süreci",
    body: "Katılımcılar kapsamlı eğitim alır:",
    items: [
      "Arsa yatırımında analiz yöntemleri",
      "İmar planlarını okuma ve fırsat bölgelerini tespit etme",
      "Bölgesel değer artışı ve yatırım potansiyeli analizi",
      "Müşteri iletişimi ve yatırım psikolojisi",
    ],
    accent: "#22346B",
    tone: "light",
  },
  {
    icon: Layers,
    title: "Portföy Eğitimi",
    body: "Katılımcılar Fırat Yapı’nın arsa geliştirme portföyünü detaylı şekilde tanır:",
    items: [
      "Mevcut projeler ve yatırım bölgeleri",
      "Fiyatlandırma ve tapu süreçleri",
      "Profesyonel sunum teknikleri",
    ],
    accent: "#8B8D93", // steel
    tone: "light",
  },
  {
    icon: Target,
    title: "Satış Eğitimi",
    body: "Başarılı bir arsa yatırım danışmanı olmak için uygulamalı satış eğitimi:",
    items: [
      "Etkili satış ve sunum teknikleri",
      "Müşteri yönlendirme ve kapanış stratejileri",
      "CRM, Bitrix24 ve WhatsApp Business kullanımı",
    ],
    accent: "#2E4590",
    tone: "light",
  },
  {
    icon: Award,
    title: "Yetkilendirme ve Kariyer Başlangıcı",
    body: "Tüm eğitimleri başarıyla tamamlayan katılımcılar Fırat Yapı Yetkili İş Ortağı unvanını kazanır. Kendi müşteri ağını oluşturabilir, profesyonel danışmanlık verebilir ve gerçekleştirdiği satışlardan yüksek komisyon geliri elde edebilir.",
    accent: "#101F4A", // navy deep
    tone: "dark",
  },
];

const PartnerProgramPage = () => {
  const { pick } = useI18n();
  const { settings } = useOutletContext() || {};
  const partnerForm = (settings?.bitrix_forms || []).find((f) => f.key === "partnership");
  const formsForBitrix = partnerForm ? [{ ...partnerForm, is_default: true }] : [];

  return (
    <div data-testid="partner-program-page" className="bg-white">
      {/* Compact breadcrumb + eyebrow */}
      <section className="pt-32 md:pt-36 pb-4 md:pb-6 bg-white">
        <div className="container-x">
          <nav
            className="flex items-center gap-2 text-[0.62rem] tracking-[0.24em] uppercase text-brand-medium"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-brand-primary transition-colors">Ana Sayfa</Link>
            <ChevronRight size={12} strokeWidth={1.5} />
            <Link to="/careers" className="hover:text-brand-primary transition-colors">Kariyer</Link>
            <ChevronRight size={12} strokeWidth={1.5} />
            <span className="text-brand-navy">İş Ortağım Programı</span>
          </nav>
        </div>
      </section>

      {/* Hero — Banner image on top, text underneath */}
      <section className="bg-white pb-6 md:pb-10" data-testid="partner-program-hero">
        <div className="container-x">
          {/* Banner image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
            className="relative w-full overflow-hidden rounded-sm shadow-[0_20px_60px_-30px_rgba(34,52,107,0.35)]"
            data-testid="partner-program-banner"
          >
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_firat-ventures/artifacts/8j9wry4q_IMG_7821.webp"
              alt="İş Ortağım Programı"
              className="w-full h-auto block"
              loading="eager"
            />
          </motion.div>

          {/* Text underneath the banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.19, 1, 0.22, 1] }}
            className="mt-8 md:mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start"
          >
            <div className="lg:col-span-8">
              <div className="flex items-center gap-4 mb-5">
                <span className="h-px w-12 bg-brand-primary" />
                <span className="text-[0.7rem] tracking-[0.36em] uppercase text-brand-primary font-medium">
                  Fırat Real Estate — İş Ortağı Programı
                </span>
              </div>
              <h1
                className="font-serif text-brand-navy leading-[1.02] tracking-tight"
                style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}
              >
                Arsa İş Ortağım Programı
              </h1>
              <p className="mt-5 text-brand-medium max-w-2xl leading-relaxed">
                <span className="font-serif italic text-brand-navy">
                  Arsa Yatırım Danışmanlığı ve Arsa Geliştirme Bir Arada.
                </span>{" "}
                Fırat Yapı’nın Arsa İş Ortağım Programı; gayrimenkul sektöründe kendi işini kurmak,
                ek gelir elde etmek veya arsa yatırım danışmanlığı alanında profesyonelleşmek
                isteyenlere eğitim, portföy ve profesyonel gelişim imkânı sunan yeni nesil bir iş
                ortaklığı sistemidir.
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="border-s-2 border-brand-primary ps-5 py-2">
                <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-primary font-medium">
                  30+ Yıl
                </div>
                <div className="font-serif text-2xl leading-tight mt-1 text-brand-navy">
                  Sektör Tecrübesi
                </div>
                <p className="mt-3 text-sm text-brand-medium leading-relaxed">
                  700’den fazla katılımcıyla Türkiye’nin en büyük arsa iş ortaklığı ekosistemine
                  siz de katılın, başarıya birlikte ulaşalım.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 7-stage vertical timeline — alternating zig-zag, connecting line, per-stage accents */}
      <section className="bg-brand-light relative overflow-hidden">
        <div className="container-x py-10 md:py-14">
          <div className="mb-8 md:mb-10 max-w-2xl">
            <div className="text-[0.66rem] tracking-[0.32em] uppercase text-brand-primary font-medium mb-3">
              Program Akışı
            </div>
            <h2
              className="font-serif text-brand-navy leading-tight"
              style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
            >
              7 Aşamada Yetkili İş Ortağı Olun
            </h2>
          </div>

          <div className="relative">
            {/* Central vertical connecting line — desktop only */}
            <div
              className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 pointer-events-none"
              style={{ background: "linear-gradient(180deg, #22346B 0%, #8B8D93 50%, #22346B 100%)", opacity: 0.25 }}
            />

            <div className="space-y-3 md:space-y-4">
              {STAGES.map((s, i) => {
                const Icon = s.icon;
                const isRight = i % 2 === 1;
                const isDark = s.tone === "dark";
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: isRight ? 40 : -40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, delay: i * 0.05, ease: [0.19, 1, 0.22, 1] }}
                    className={`relative md:grid md:grid-cols-2 md:gap-8 ${isRight ? "" : ""}`}
                    data-testid={`stage-${i + 1}`}
                  >
                    {/* Content card */}
                    <div className={`${isRight ? "md:col-start-2" : "md:col-start-1"}`}>
                      <div
                        className={`relative p-6 md:p-8 border transition-transform duration-500 hover:-translate-y-1 ${
                          isDark
                            ? "text-white border-transparent"
                            : "text-brand-dark border-black/5 bg-white"
                        }`}
                        style={
                          isDark
                            ? { background: `linear-gradient(135deg, ${s.accent} 0%, #101F4A 100%)` }
                            : {}
                        }
                      >
                        {/* Accent edge */}
                        <span
                          className={`absolute top-0 ${isRight ? "right-0" : "left-0"} bottom-0 w-1`}
                          style={{ background: s.accent }}
                        />

                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 flex flex-col items-center">
                            <span
                              className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                              style={{ background: s.accent, color: "#fff" }}
                            >
                              <Icon size={18} strokeWidth={1.5} />
                            </span>
                            <span
                              className={`mt-2 text-[0.62rem] tracking-[0.22em] font-medium ${
                                isDark ? "text-white/70" : "text-brand-primary"
                              }`}
                            >
                              {String(i + 1).padStart(2, "0")}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div
                              className={`font-serif text-xl md:text-2xl leading-tight ${
                                isDark ? "text-white" : "text-brand-navy"
                              }`}
                            >
                              {s.title}
                            </div>
                            <p
                              className={`mt-3 text-sm md:text-[0.9rem] leading-[1.65] ${
                                isDark ? "text-white/80" : "text-brand-dark"
                              }`}
                            >
                              {s.body}
                            </p>
                            {s.items ? (
                              <ul className="mt-3 space-y-1.5">
                                {s.items.map((it, k) => (
                                  <li
                                    key={k}
                                    className={`flex items-start gap-2 text-[0.82rem] leading-snug ${
                                      isDark ? "text-white/85" : "text-brand-medium"
                                    }`}
                                  >
                                    <span
                                      className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                                      style={{ background: isDark ? "#fff" : s.accent }}
                                    />
                                    <span>{it}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Center number bubble on the line (desktop) */}
                    <div className="hidden md:block absolute left-1/2 top-8 -translate-x-1/2 z-10">
                      <span
                        className="w-8 h-8 rounded-full flex items-center justify-center text-[0.7rem] font-semibold text-white shadow-md ring-4 ring-brand-light"
                        style={{ background: s.accent }}
                      >
                        {i + 1}
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Trust callout */}
      <section className="bg-brand-navy text-white">
        <div className="container-x py-8 md:py-12">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-4">
              <Handshake size={18} strokeWidth={1.5} className="text-brand-primary-bright" />
              <span className="text-[0.66rem] tracking-[0.36em] uppercase text-brand-primary-bright font-medium">
                Birlikte Büyüyen Güçlü Ekip
              </span>
            </div>
            <h3
              className="font-serif leading-[1.1]"
              style={{ fontSize: "clamp(1.75rem, 3.4vw, 2.75rem)" }}
            >
              30 yılı aşkın sektör deneyimiyle Fırat Yapı, iş ortaklarına yalnızca eğitim sunmakla
              kalmaz; uzun vadeli başarıya ulaşmaları için güçlü bir iş modeli ve profesyonel bir
              çalışma ekosistemi sağlar.
            </h3>
          </div>
        </div>
      </section>

      {/* Bitrix form — İş Ortaklığı */}
      <section id="basvuru" className="bg-white">
        <div className="container-x py-8 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
          <div className="lg:col-span-4">
            <div className="text-[0.66rem] tracking-[0.32em] uppercase text-brand-primary font-medium mb-3">
              Hemen Başvurun
            </div>
            <h2
              className="font-serif text-brand-navy leading-[1.05]"
              style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}
            >
              Arsa İş Ortağım Programı’na Katılın
            </h2>
            <p className="mt-4 text-brand-medium leading-relaxed">
              Aşağıdaki başvuru formunu doldurarak programa katılabilirsiniz. Ekibimiz başvurunuzu
              inceledikten sonra sizinle iletişime geçecektir.
            </p>
          </div>
          <div className="lg:col-span-8">
            {formsForBitrix.length ? (
              <BitrixContactForm forms={formsForBitrix} />
            ) : (
              <div className="border border-black/10 p-6 bg-brand-light text-sm text-brand-medium">
                İş Ortaklığı Bitrix formu henüz Site Ayarları’nda tanımlı değil.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnerProgramPage;
